import { useCallback, useMemo } from "react";
import { Flex, IconButton, Text } from "@radix-ui/themes";
import { Cross1Icon } from "@radix-ui/react-icons";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BooleanFilterMenuItemType } from "../FilterMenu.model";
import {
  EqualFilter,
  isNullFilter,
  IsNullFilter,
  NotEqualFilter,
} from "../FieldFilter.model";
import styles from "../FilterMenu.module.scss";

interface BooleanOptionsProps {
  trueLabel: string;
  falseLabel: string;
  selected?: boolean;
  onClick: (value: boolean) => void;
}

function BooleanOptions({
  children,
  trueLabel,
  falseLabel,
  selected,
  onClick,
}: React.PropsWithChildren<BooleanOptionsProps>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>

      <DropdownMenuContent align="start">
        <DropdownMenuCheckboxItem
          checked={selected === true}
          onCheckedChange={() => onClick(true)}
        >
          {trueLabel}
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={selected === false}
          onCheckedChange={() => onClick(false)}
        >
          {falseLabel}
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface BooleanActiveFilterItemProps<Field extends string>
  extends BooleanFilterMenuItemType<Field> {
  value: EqualFilter | NotEqualFilter | IsNullFilter;
  size: "1" | "2" | "3";
  viewOnly: boolean;
  onChange: (item: BooleanFilterMenuItemType<Field>) => void;
}

export function BooleanActiveFilterItem<Field extends string>({
  size,
  viewOnly,
  onChange,
  ...item
}: BooleanActiveFilterItemProps<Field>) {
  const trueLabel = item.trueLabel ?? "Yes";
  const falseLabel = item.falseLabel ?? "No";

  const selectedValue = useMemo(() => {
    if (isNullFilter(item.value)) return null;
    return item.value?.value as boolean | undefined;
  }, [item.value]);

  const handleOperatorChange = useCallback(
    (operator: "eq" | "ne") => {
      if (isNullFilter(item.value)) {
        return;
      }

      onChange({
        ...item,
        value: {
          operator,
          value: item.value?.value,
        },
      });
    },
    [item, onChange]
  );

  const handleValueClick = useCallback(
    (value: boolean) => {
      if (isNullFilter(item.value)) {
        onChange({
          ...item,
          value: {
            operator: "eq",
            value,
          },
        });
        return;
      }

      onChange({
        ...item,
        value: {
          operator: item.value?.operator ?? "eq",
          value,
        },
      });
    },
    [item, onChange]
  );

  const handleClear = useCallback(
    () => onChange({ ...item, value: undefined }),
    [item, onChange]
  );

  if (isNullFilter(item.value)) {
    return (
      <Flex gap={"1px"} className={styles.activeFilter} align="center">
        <div
          data-accent-color="gray"
          className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`}
        >
          {item.icon}
          {item.label}
        </div>

        <div
          data-accent-color="gray"
          className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`}
        >
          is
        </div>

        <BooleanOptions
          trueLabel={trueLabel}
          falseLabel={falseLabel}
          selected={selectedValue ?? undefined}
          onClick={handleValueClick}
        >
          <div
            data-accent-color="gray"
            className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button ${
              viewOnly ? "pointer-events-none" : ""
            } shrink`}
          >
            Unknown
          </div>
        </BooleanOptions>

        {!viewOnly && (
          <IconButton
            variant="soft"
            color="gray"
            size={size}
            onClick={handleClear}
          >
            <Cross1Icon />
          </IconButton>
        )}
      </Flex>
    );
  }

  if (selectedValue == null) return <></>;

  return (
    <Flex gap={"1px"} className={styles.activeFilter} align="center">
      <div
        data-accent-color="gray"
        className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`}
      >
        {item.icon}
        {item.label}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div
            data-accent-color="gray"
            className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button ${
              viewOnly ? "pointer-events-none" : ""
            }`}
          >
            {item.value?.operator === "eq" ? "is" : "is not"}
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start">
          <DropdownMenuCheckboxItem
            checked={item.value?.operator === "eq"}
            onCheckedChange={() => handleOperatorChange("eq")}
          >
            is
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={item.value?.operator === "ne"}
            onCheckedChange={() => handleOperatorChange("ne")}
          >
            is not
          </DropdownMenuCheckboxItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <BooleanOptions
        trueLabel={trueLabel}
        falseLabel={falseLabel}
        selected={selectedValue ?? undefined}
        onClick={handleValueClick}
      >
        <div
          data-accent-color="gray"
          className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button ${
            viewOnly ? "pointer-events-none" : ""
          } shrink`}
        >
          <Text truncate>{selectedValue ? trueLabel : falseLabel}</Text>
        </div>
      </BooleanOptions>

      {!viewOnly && (
        <IconButton
          variant="soft"
          color="gray"
          size={size}
          onClick={handleClear}
        >
          <Cross1Icon />
        </IconButton>
      )}
    </Flex>
  );
}
